import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Clock,
  Plus,
  Edit3,
  Trash2,
  X,
  Check,
  AlertCircle
} from 'lucide-react';

const ContactCenter = () => {
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const [formData, setFormData] = useState({
    address: '',
    phone: '',
    email: '',
    office_hours: ''
  });

  const API_URL = 'https://metapi.emedhatechnologies.com/api/students/contacts';

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(API_URL);
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddNewClick = () => {
    setIsEditing(false);
    setFormData({ address: '', phone: '', email: '', office_hours: '' });
    setShowForm(true);
  };

  const handleEditClick = (contact) => {
    setIsEditing(true);
    setCurrentId(contact.id);
    setFormData({
      address: contact.address,
      phone: contact.phone,
      email: contact.email,
      office_hours: contact.office_hours
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/${currentId}`, formData);
      } else {
        await axios.post(API_URL, formData);
      }
      setShowForm(false);
      fetchContacts();
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        fetchContacts();
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    }
  };

  return (
    <div >
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.titleWrapper}>
          <div style={styles.iconBox}>
            <Building2 size={24} color="#6366f1" />
          </div>
          <div>
            <h2 style={styles.title}>Institution Contact Management</h2>
            <p style={styles.subtitle}>Manage institutional branches, departments, and working hours.</p>
          </div>
        </div>

        {!showForm && (
          <button onClick={handleAddNewClick} style={styles.primaryButton}>
            <Plus size={18} /> Add New Contact
          </button>
        )}
      </div>

      {/* Form Overlay / Section */}
      {showForm && (
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h3>{isEditing ? 'Edit Contact Entry' : 'Add New Contact Entry'}</h3>
            <button onClick={() => setShowForm(false)} style={styles.closeButton}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}><MapPin size={14} /> Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                rows="3"
                style={styles.textarea}
                placeholder="Enter complete office location..."
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}><Phone size={14} /> Phone Numbers</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                style={styles.input}
                placeholder="e.g., +91-4565-241386"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}><Mail size={14} /> Email IDs</label>
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={styles.input}
                placeholder="e.g., cdoe@edulearner.ac.in"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}><Clock size={14} /> Office Hours</label>
              <input
                type="text"
                name="office_hours"
                value={formData.office_hours}
                onChange={handleInputChange}
                required
                style={styles.input}
                placeholder="e.g., Mon – Fri: 9:30 AM – 5:30 PM"
              />
            </div>

            <div style={styles.formActions}>
              <button type="submit" style={styles.submitButton}>
                <Check size={16} /> {isEditing ? 'Update Entry' : 'Save Entry'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Data Presentation Container */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Address</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Office Hours</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length > 0 ? (
              contacts.map((item, index) => (
                <tr key={item.id} style={{ ...styles.tr, background: index % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                  <td style={styles.td}><strong>#{item.id}</strong></td>
                  <td style={{ ...styles.td, whiteSpace: 'pre-line' }}>{item.address}</td>
                  <td style={styles.td}>{item.phone}</td>
                  <td style={styles.td}><span style={styles.emailBadge}>{item.email}</span></td>
                  <td style={styles.td}>{item.office_hours}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={styles.actionButtonGroup}>
                      <button onClick={() => handleEditClick(item)} style={styles.editButton} title="Edit">
                        <Edit3 size={16} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} style={styles.deleteButton} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={styles.emptyState}>
                  <AlertCircle size={40} color="#94a3b8" />
                  <p style={styles.emptyText}>No contact records found.</p>
                  <p style={styles.emptySubText}>Click "Add New Contact" above to set up your first record.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Aesthetic Clean Modern Theme Stylesheet
const styles = {

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  iconBox: {
    backgroundColor: '#e0e7ff',
    padding: '12px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '4px 0 0 0',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px',
    boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.2)',
    transition: 'background-color 0.2s',
  },
  formCard: {
    backgroundColor: '#ffffff',
    padding: '28px',
    borderRadius: '16px',
    marginBottom: '32px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025)',
    border: '1px solid #e2e8f0',
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: '12px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  input: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: '#fff',
    transition: 'border-color 0.2s',
  },
  textarea: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    backgroundColor: '#fff',
  },
  formActions: {
    gridColumn: '1 / -1',
    display: 'flex',
    gap: '12px',
    marginTop: '10px',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px',
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  tableHeaderRow: {
    backgroundColor: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
  },
  th: {
    padding: '14px 18px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tr: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background-color 0.15s',
  },
  td: {
    padding: '16px 18px',
    fontSize: '14px',
    color: '#334155',
    verticalAlign: 'middle',
  },
  emailBadge: {
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
  },
  actionButtonGroup: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  editButton: {
    padding: '8px',
    backgroundColor: '#fef3c7',
    color: '#d97706',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButton: {
    padding: '8px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#475569',
    margin: '12px 0 4px 0',
  },
  emptySubText: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
  },
};

export default ContactCenter;